/**
 * Shared Constants
 */
// Fonte única de formas de pagamento (Story 44.10 / ADR-0001) — arquivo próprio.
export * from './payment-methods.js';
export * from './laser-regions.js';
export * from './business-hours.js';
export * from './message-dispatch.js';
// Appointment status — valid unidirectional transitions (status never goes back).
// Exceção única e auditada (43.12): cancelled → pending é a REATIVAÇÃO de um
// cancelamento por engano (guards de futuro + conflito no service). completed e
// no_show seguem terminais (envolvem pagamento/comissão).
export const APPOINTMENT_TRANSITIONS = {
    pending: ['confirmed', 'cancelled', 'no_show', 'completed'],
    confirmed: ['in_progress', 'no_show', 'cancelled'],
    in_progress: ['completed', 'cancelled'],
    completed: [],
    cancelled: ['pending'],
    no_show: [],
};
export const APPOINTMENT_TERMINAL_STATUSES = ['completed', 'cancelled', 'no_show'];
// Duração mínima de agendamento (min) — alinhada ao @Min(5) do cadastro de serviço.
// Fonte única BE+FE; o antigo mínimo de 15 tornava serviços de 5–14 min impossíveis
// de agendar (incidente em produção, Onda A).
export const MIN_APPOINTMENT_DURATION_MINUTES = 5;
// Teto do horário definido pela usuária (hotfix duração) — o mesmo @Max dos DTOs de agendamento.
export const MAX_APPOINTMENT_DURATION_MINUTES = 480;
// Appointment status colors (matching UX design tokens)
export const APPOINTMENT_STATUS_COLORS = {
    pending: '#eab308', // Yellow
    confirmed: '#22c55e', // Green
    in_progress: '#3b82f6', // Blue
    completed: '#6b7280', // Gray
    cancelled: '#ef4444', // Red
    no_show: '#1f2937', // Dark
};
// Patient status colors
export const PATIENT_STATUS_COLORS = {
    active: '#22c55e', // Green
    inactive: '#6b7280', // Gray
    blocked: '#ef4444', // Red
};
// Lead Kanban default columns
export const DEFAULT_KANBAN_COLUMNS = [
    { name: 'Lead', color: '#5e81f4', order: 0, isDefault: true },
    { name: 'Primeiro contato', color: '#3b82f6', order: 1, isDefault: false },
    { name: 'Qualificação', color: '#8b5cf6', order: 2, isDefault: false },
    { name: 'Avaliação agendada', color: '#f59e0b', order: 3, isDefault: false },
    { name: 'Avaliação realizada', color: '#06b6d4', order: 4, isDefault: false },
    { name: 'Plano enviado', color: '#eab308', order: 5, isDefault: false },
    { name: 'Fechamento', color: '#22c55e', order: 6, isDefault: false },
    { name: 'Em tratamento', color: '#14b8a6', order: 7, isDefault: false },
];
// Story 66.2 — funis de marketing: etapas de "Começar do zero" e limites por clínica.
export const MARKETING_FUNNEL_DEFAULT_STAGES = ['Entrada', 'Convite enviado', 'Respondeu', 'Sucesso', 'Não quis'];
export const MARKETING_FUNNEL_MAX_ACTIVE = 20;
export const MARKETING_FUNNEL_MAX_STAGES = 10;
// Working hours defaults
export const DEFAULT_WORKING_HOURS = {
    monday: { start: '08:00', end: '18:00' },
    tuesday: { start: '08:00', end: '18:00' },
    wednesday: { start: '08:00', end: '18:00' },
    thursday: { start: '08:00', end: '18:00' },
    friday: { start: '08:00', end: '18:00' },
    saturday: { start: '08:00', end: '12:00' },
    sunday: null,
};
// Default services for onboarding
export const DEFAULT_SERVICES = [
    { name: 'Laser Axila', duration: 30, price: 15000 },
    { name: 'Laser Virilha', duration: 30, price: 20000 },
    { name: 'Laser Perna Inteira', duration: 60, price: 40000 },
    { name: 'Laser Rosto', duration: 30, price: 25000 },
    { name: 'Laser Corpo Inteiro', duration: 120, price: 80000 },
];
// Lead origin options for dropdowns (type-linked to LeadOrigin)
export const LEAD_ORIGINS = [
    { value: 'manual', label: 'Manual' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'website', label: 'Website' },
    { value: 'import', label: 'Importação' },
    { value: 'referral', label: 'Indicação' },
];
// Brazilian states
export const BRAZILIAN_STATES = [
    { code: 'AC', name: 'Acre' },
    { code: 'AL', name: 'Alagoas' },
    { code: 'AP', name: 'Amapá' },
    { code: 'AM', name: 'Amazonas' },
    { code: 'BA', name: 'Bahia' },
    { code: 'CE', name: 'Ceará' },
    { code: 'DF', name: 'Distrito Federal' },
    { code: 'ES', name: 'Espírito Santo' },
    { code: 'GO', name: 'Goiás' },
    { code: 'MA', name: 'Maranhão' },
    { code: 'MT', name: 'Mato Grosso' },
    { code: 'MS', name: 'Mato Grosso do Sul' },
    { code: 'MG', name: 'Minas Gerais' },
    { code: 'PA', name: 'Pará' },
    { code: 'PB', name: 'Paraíba' },
    { code: 'PR', name: 'Paraná' },
    { code: 'PE', name: 'Pernambuco' },
    { code: 'PI', name: 'Piauí' },
    { code: 'RJ', name: 'Rio de Janeiro' },
    { code: 'RN', name: 'Rio Grande do Norte' },
    { code: 'RS', name: 'Rio Grande do Sul' },
    { code: 'RO', name: 'Rondônia' },
    { code: 'RR', name: 'Roraima' },
    { code: 'SC', name: 'Santa Catarina' },
    { code: 'SP', name: 'São Paulo' },
    { code: 'SE', name: 'Sergipe' },
    { code: 'TO', name: 'Tocantins' },
];
// Fitzpatrick skin types
export const FITZPATRICK_SKIN_TYPES = [
    { type: 1, description: 'Pele muito clara, sempre queima, nunca bronzeia' },
    { type: 2, description: 'Pele clara, queima facilmente, bronzeia minimamente' },
    { type: 3, description: 'Pele morena clara, queima moderadamente, bronzeia gradualmente' },
    { type: 4, description: 'Pele morena, raramente queima, bronzeia facilmente' },
    { type: 5, description: 'Pele morena escura, muito raramente queima, bronzeia muito facilmente' },
    { type: 6, description: 'Pele negra, nunca queima, sempre bronzeia' },
];
// Campaign status colors
export const CAMPAIGN_STATUS_COLORS = {
    draft: '#6b7280', // Gray
    scheduled: '#eab308', // Yellow
    sending: '#3b82f6', // Blue
    completed: '#22c55e', // Green
    cancelled: '#ef4444', // Red
};
// Bill status colors
export const BILL_STATUS_COLORS = {
    pending: '#eab308', // Yellow
    paid: '#22c55e', // Green
    overdue: '#ef4444', // Red
    cancelled: '#6b7280', // Gray
};
// Transaction status colors
export const TRANSACTION_STATUS_COLORS = {
    pending: '#eab308', // Yellow
    paid: '#22c55e', // Green
    overdue: '#ef4444', // Red
    cancelled: '#6b7280', // Gray
};
// API rate limits
export const RATE_LIMITS = {
    default: { points: 100, duration: 60 }, // 100 requests per minute
    auth: { points: 10, duration: 60 }, // 10 auth attempts per minute
    whatsapp: { points: 30, duration: 60 }, // 30 WhatsApp messages per minute
};
// JWT configuration
export const JWT_CONFIG = {
    accessTokenExpiresIn: '15m',
    refreshTokenExpiresIn: '7d',
};
// Pagination defaults
export const PAGINATION = {
    defaultLimit: 20,
    maxLimit: 100,
};
// Gate 42.4 (RC-015): contrato único do evento tempo-real de appointment — api emite,
// web assina; string literal duplicada = drift silencioso.
export const APPOINTMENT_UPDATED_EVENT = 'appointment.updated';
// RC-065: Configurações da clínica salvas → todas as abas do tenant trocam o fuso (e o resto) sem novo login.
export const CLINIC_SETTINGS_UPDATED_EVENT = 'settings:updated';
//# sourceMappingURL=index.js.map